import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
// (Link still used by the back-to-settings link at the top of the page)
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { env } from '../lib/env.js';
import { brandsQuery } from '../lib/queries.js';

// /brand/$slug/google-connect/error — brand-aware version of the
// google-connect error page. Same shape as the meta version (s191.3):
// the api redirects here when an OAuth failure happens AFTER state
// has been verified, so we know which brand the founder was
// connecting and can deep-link the retry / back actions to that brand.

const searchSchema = z.object({
  detail: z.string().optional(),
});

export const Route = createFileRoute('/brand/$slug/google-connect/error')({
  validateSearch: (s) => searchSchema.parse(s),
  component: BrandGoogleConnectErrorPage,
});

function BrandGoogleConnectErrorPage() {
  const { slug } = Route.useParams();
  const { detail } = Route.useSearch();
  const copy = renderErrorCopy(detail);
  const brandsQ = useQuery(brandsQuery());
  const brand = brandsQ.data?.find((b) => b.slug === slug) ?? null;
  const retryUrl = brand ? `${env.API_URL}/api/brands/${brand.id}/google/connect/start` : null;

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <section className="mx-auto max-w-[640px] px-4 pb-16 pt-12 sm:px-7">
        <Link
          to="/brand/$slug/channels"
          params={{ slug }}
          className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to channels</Trans>
        </Link>
        <div className="mt-6 flex items-start gap-3">
          <Mag size={44} mood="default" />
          <div className="flex-1">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>connect google ads</Trans>
            </p>
            <h1 className="mt-1 font-display text-[28px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[34px]">
              {copy.title}
            </h1>
            <p className="mt-4 font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
              “{copy.message}”
            </p>
            {/* "↘ open settings" removed — the back link at the top
                of the page already covers that destination, putting
                it next to the primary action duplicated the affordance
                without adding clarity. */}
            <div className="mt-6 flex items-center justify-end">
              {retryUrl ? (
                <a href={retryUrl} className="btn">
                  <Trans>try connecting again</Trans>
                  <span className="btn-arrow">→</span>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function renderErrorCopy(detail?: string): { title: string; message: string } {
  switch (detail) {
    case 'state_expired':
      return {
        title: 'this took too long.',
        message: "the connect handshake timed out — try again from settings, i'll be quick.",
      };
    case 'invalid_state':
      return {
        title: 'something tampered with the link.',
        message:
          'the connect link was modified between starting and finishing. try again from a fresh tab.',
      };
    case 'oauth_not_configured':
      return {
        title: "mag isn't ready to connect yet.",
        message:
          "the google oauth credentials haven't been wired on the api side. tell me to set them up and try again.",
      };
    case 'missing_params':
      return {
        title: "google didn't send back the right pieces.",
        message: 'the redirect was missing code or state. start over from settings.',
      };
    case 'oauth_failed':
      return {
        title: 'google refused.',
        message:
          "they didn't grant the scope — most often that's because the OAuth consent screen isn't verified for the adwords scope yet, or you declined a permission.",
      };
    // s357 — this used to fall into oauth_failed, which told the
    // founder they'd declined a permission they had actually just
    // granted. Nothing is wrong on their side: google retired the
    // API version we call, and only we can fix it.
    case 'api_version_sunset':
      return {
        title: 'my side is out of date.',
        message:
          "your permission went through fine — google retired the version of their ads api i'm calling, so i couldn't read your accounts. that's on me to update, not on you. i'm on it.",
      };
    // s371 — this copy existed for months and nothing ever triggered it.
    // The empty-customer-list case it was written for is handled on the
    // picker; what actually happens when a founder has no Google Ads
    // account is that google 401s the customer lookup, which landed
    // here as 'oauth_failed' — "you declined a permission" — to a
    // founder who had just granted it.
    case 'no_customers':
      return {
        title: 'no google ads account on that login.',
        message:
          "your permission went through fine. google just has no ads account attached to that email — which is normal if you haven't made one yet. create it at ads.google.com, then come back and connect.",
      };
    default:
      return {
        title: 'something broke during connect.',
        message: detail
          ? `the api said: ${detail}. try again, or tell me about it.`
          : "i don't know exactly what — try again, or tell me about it.",
      };
  }
}
